import {computed, Injectable, signal, Type} from '@angular/core';
import {Observable, Subject} from 'rxjs';

/** The drawer to mount, and the channels the opener listens on. */
export interface RightSliderDrawer {
	component: Type<unknown>;
	data?: unknown;
	onOutputEvent?: Subject<unknown>;
	onClose: Subject<unknown>;
}

@Injectable({providedIn: 'root'})
export class RightSliderService {

	private readonly _open = signal(false);
	/** Whether the drawer is on screen. */
	readonly open = this._open.asReadonly();

	protected componentChange = new Subject<RightSliderDrawer>();

	private readonly closeRequest = new Subject<void>();

	private currentClose: Subject<unknown> | null = null;

	// Navigation history of the drawer. The last entry is always the drawer
	// currently on screen, so going back means dropping it and showing the one
	// underneath. Reset on close: a drawer opened from a page starts a new trail.
	private readonly history = signal<{component: Type<unknown>, data?: unknown}[]>([]);

	/** True when a drawer sits underneath the one on screen. */
	readonly canGoBack = computed(() => this.history().length > 1);

	/**
	 * The drawer on screen has a comments panel (it declares `static hasCommentsPanel`). A flag rather than a class
	 * check: importing the application drawer here would pull its whole tree into the first bundle.
	 */
	readonly canNavigateToComment = computed(() =>
		(this.history().at(-1)?.component as {hasCommentsPanel?: boolean} | undefined)?.hasCommentsPanel === true);

	openComponent<U>(component: Type<any>, data?: U, onOutputEvent?: Subject<any>): Observable<any> {
		if (this.isCurrentEntry(component, data)) {
			// Re-showing the drawer already on screen would destroy and recreate it,
			// wiping its scroll, active tab and pending edits. Hand back the close of
			// the live instance instead so the caller still gets its result.
			if (this._open() && this.currentClose) {
				return this.currentClose.asObservable();
			}
		} else {
			this.history.update(history => [...history, {component, data}]);
		}
		return this.show(component, data, onOutputEvent);
	}

	private isCurrentEntry(component: Type<unknown>, data: unknown): boolean {
		const current = this.history().at(-1);
		return !!current && current.component === component && this.sameData(current.data, data);
	}

	/** Shallow compare: drawer inputs are flat id bags ({applicationId}, {tenantId, serverId}, …). */
	private sameData(a: unknown, b: unknown): boolean {
		if (Object.is(a, b)) { return true; }
		if (!a || !b || typeof a !== 'object' || typeof b !== 'object') { return false; }
		const keysA = Object.keys(a), keysB = Object.keys(b);
		return keysA.length === keysB.length
			&& keysA.every(k => Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
	}

	private show(component: Type<unknown>, data?: unknown, onOutputEvent?: Subject<unknown>): Observable<unknown> {
		const onClose = new Subject<unknown>();
		this.currentClose = onClose;
		this._open.set(true);
		this.componentChange.next({component, data, onOutputEvent, onClose});
		return onClose.asObservable();
	}

	/**
	 * Goes back to the drawer that opened the current one.
	 *
	 * The previous component is re-created from the data it was opened with, so
	 * its own state (scroll, active tab, pending edits) is not restored.
	 */
	back(): void {
		if (!this.canGoBack()) {
			return;
		}
		this.history.update(history => history.slice(0, -1));
		const previous = this.history().at(-1)!;
		// Deliberately not openComponent(): that would push the entry back on and
		// the history would never shrink.
		this.show(previous.component, previous.data);
	}

	isOpened(): boolean {
		return this._open();
	}

	onComponentChange() : Observable<RightSliderDrawer> {
		return this.componentChange.asObservable();
	}

	/** Emits on every close() so the host can tear down the component it mounted. */
	onCloseRequest(): Observable<void> {
		return this.closeRequest.asObservable();
	}

	close(): void {
		this.history.set([]);
		this._open.set(false);
		this.currentClose = null;
		this.closeRequest.next();
	}
}
